import { useState, useEffect } from "react";

import {
    json,
    type ActionFunctionArgs,
    type LoaderFunction,
} from "@remix-run/node";
import {
    Link,
    useNavigate,
    useSearchParams,
    useFetcher,
} from "@remix-run/react";

import Layout from "~/components/layout";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { A, P } from "~/components/ui/typography";
import { withCors } from "~/config/cors";
import { createSupabaseServerClient } from "~/integrations/supabase/ssr.server";
import { PRODUCT_NAME } from "~/lib/env.shared";
import { getLogger } from "~/modules.server/logger";
import { pageMeta } from "~/utils/meta";
import { SUPPORT_EMAIL, SERVER_URL } from "~/lib/env.shared";
import { assertIsPost } from "~/utils.server/http.server";

export const meta = pageMeta(
    `Reset Password - ${PRODUCT_NAME}`,
    `Reset your ${PRODUCT_NAME} password`,
);

export const loader: LoaderFunction = withCors(async () => json({}));

export const action = withCors(async ({ request }: ActionFunctionArgs) => {
    assertIsPost(request);
    const formData = await request.formData();

    const email = formData.get("email") as string;

    // Use SSR server client
    const { supabase, headers } = createSupabaseServerClient(request);

    const redirectUrl = `${SERVER_URL}/reset-password-confirm`;
    getLogger()
        .withMetadata({
            url: redirectUrl,
        })
        .debug("Reset password for email", email);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
    });

    if (error) {
        return json({ error: error.message }, { status: 400, headers });
    }

    return json({ success: true }, { headers });
});

export default function ResetPasswordRoute() {
    const navigate = useNavigate();
    const fetcher = useFetcher<{ error?: string; success?: boolean }>();
    const [searchParams] = useSearchParams();
    const [isSubmitted, setIsSubmitted] = useState(false);

    const message = searchParams.get("message");
    const isSubmitting = fetcher.state === "submitting";

    // Check if action succeeded
    useEffect(() => {
        if (fetcher.data?.success) {
            setIsSubmitted(true);
        }
    }, [fetcher.data]);

    if (isSubmitted) {
        return (
            <Layout
                backNavOverride="/login"
                title="Check your email"
                offsetTop={false}
                narrow={true}
            >
                <div className="mx-auto w-full max-w-sm space-y-6">
                    <div className="space-y-2 text-center">
                        <P className="">
                            We've sent you a password reset link. Please check
                            your email and follow the instructions.
                        </P>
                    </div>
                    <div className="text-center">
                        <Button
                            variant="ghost"
                            onClick={() => navigate("/login")}
                        >
                            Return to login
                        </Button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout
            offsetTop={false}
            narrow={true}
            backNavOverride="/login"
            title="Reset your password"
        >
            <div className="mx-auto w-full max-w-sm space-y-6">
                {message && (
                    <div className="text-center">
                        <P className="text-sm text-muted-foreground">
                            {message}
                        </P>
                    </div>
                )}

                <fetcher.Form
                    method="POST"
                    action="/reset-password"
                    className="space-y-4"
                >
                    <div>
                        <Input
                            name="email"
                            type="email"
                            placeholder="name@example.com"
                            required
                        />
                    </div>

                    {fetcher.data?.error && (
                        <P className="text-sm text-destructive">
                            {fetcher.data.error}
                        </P>
                    )}

                    <Button
                        type="submit"
                        className="w-full"
                        isLoading={isSubmitting}
                    >
                        Send reset link
                    </Button>
                    <Link to="/login" className="block">
                        <Button variant="ghost" className="w-full">
                            Back to sign in
                        </Button>
                    </Link>
                    <P className="text-center text-xs">
                        Need help?{" "}
                        <A
                            className="font-medium text-rose-500"
                            href={`mailto:${SUPPORT_EMAIL}`}
                            target="_blank"
                        >
                            {SUPPORT_EMAIL}
                        </A>
                    </P>
                </fetcher.Form>

                <div className="text-center text-sm"></div>
            </div>
        </Layout>
    );
}
